'use client';

import { useEffect } from 'react';

type WithCookiebot = typeof globalThis & {
  Cookiebot?: { hasResponse?: boolean; dialog?: { visible?: boolean; DOM?: Element | null }; show?: () => void };
};

/**
 * Cookiebot can add its banner to <body> before React hydrates. React then finds an element it
 * didn't render where it expected its own <div>, rebuilds that part of the page and drops the
 * banner, so visitors see it flash and vanish. This puts it back.
 *
 * Only when Cookiebot still thinks the banner is showing but its element has left the page: a
 * visitor Cookiebot chose not to prompt (dialog.visible false) is never shown one.
 */
export const CookieBannerRecovery = () => {
  // Synchronises with Cookiebot and the DOM: watches <body> and restores a dropped banner.
  useEffect(() => {
    const observer = new MutationObserver(() => restore());

    function restore() {
      const cookiebot = (globalThis as WithCookiebot).Cookiebot;
      if (cookiebot?.hasResponse) {
        observer.disconnect();
        return;
      }
      if (cookiebot?.dialog?.visible && cookiebot.dialog.DOM && !cookiebot.dialog.DOM.isConnected) {
        cookiebot.show?.();
      }
    }

    restore();
    observer.observe(document.body, { childList: true });
    return () => observer.disconnect();
  }, []);

  return null;
};
